import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { FormLabel } from "@components";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/20/solid";

// A searchable, floating dropdown built on HeadlessUI's Combobox so the options portal above the
// modal (never clipped) and get proper keyboard nav. Two data modes:
//   - async:  pass `search(term, { signal })` returning a promise of options
//   - static: pass `options` + `filterOption(option, term)` to filter an already-loaded list
// Results are sorted alphabetically by `sortBy(option)` (falls back to `displayValue`).
// Set `multiple` to select several options: `value` becomes an array, picks toggle membership,
// selected options render as removable chips, and the dropdown stays open for more picks.
export default function SearchSelect({
  label,
  placeholder,
  value,
  onSelect,
  displayValue,
  getKey,
  renderOption,
  sortBy,
  search,
  queryKey,
  options,
  filterOption,
  disabled = false,
  emptyText = "No results",
  multiple = false,
  autoFocus = false,
  error = null,
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [fieldWidth, setFieldWidth] = useState(null);
  const fieldRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Match the dropdown width to the field itself (measured directly, so it can't collapse to
  // content width or stop short at the trailing icons).
  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    const update = () => setFieldWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isStatic = options !== undefined;

  const { data: asyncData, isFetching } = useQuery({
    queryKey: [...(queryKey ?? []), debounced],
    queryFn: ({ signal }) => search(debounced, { signal }),
    enabled: !isStatic && !disabled,
  });

  const results = useMemo(() => {
    const list = isStatic
      ? (options ?? []).filter((o) => (debounced ? filterOption(o, debounced) : true))
      : (asyncData ?? []);
    const key = sortBy ?? ((o) => (displayValue ? String(displayValue(o)) : ""));
    return [...list].sort((a, b) =>
      String(key(a)).localeCompare(String(key(b)), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [isStatic, options, asyncData, debounced, filterOption, sortBy, displayValue]);

  const loading = !isStatic && isFetching;
  const sameOption = (a, b) => (a && b ? getKey(a) === getKey(b) : a === b);
  const selected = multiple ? (value ?? []) : value;

  const removeOption = (option) => {
    onSelect(selected.filter((o) => !sameOption(o, option)));
  };

  return (
    <div>
      {label && <FormLabel>{label}</FormLabel>}
      {multiple && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((option) => (
            <span
              key={getKey(option)}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 pl-2.5 pr-1.5 py-1 text-xs font-medium text-primary-700"
            >
              {displayValue(option)}
              <button
                type="button"
                aria-label={`Remove ${displayValue(option)}`}
                onClick={() => removeOption(option)}
                className="rounded-full p-0.5 hover:bg-primary-100"
              >
                <XMarkIcon className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Combobox
        value={selected}
        by={sameOption}
        multiple={multiple}
        onChange={(v) => {
          onSelect(v);
          if (multiple) setQuery("");
        }}
        onClose={() => setQuery("")}
        disabled={disabled}
        immediate
      >
        <div ref={fieldRef} className="relative">
          <ComboboxInput
            autoFocus={autoFocus}
            className="w-full rounded-xl border border-zinc-200 bg-white py-2.5 pl-3.5 pr-16 text-sm text-zinc-900 shadow-sm transition placeholder:text-zinc-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-zinc-50 disabled:text-zinc-400"
            placeholder={placeholder}
            displayValue={(v) => (multiple ? "" : v ? displayValue(v) : "")}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
            {!multiple && value && (
              <button
                type="button"
                aria-label="Clear selection"
                onClick={() => {
                  onSelect(null);
                  setQuery("");
                }}
                className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
              >
                <XMarkIcon className="size-4" />
              </button>
            )}
            <ComboboxButton className="rounded-md p-1 text-zinc-400 hover:text-zinc-600">
              <ChevronUpDownIcon className="size-5" />
            </ComboboxButton>
          </div>
        </div>

        <ComboboxOptions
          anchor="bottom start"
          transition
          style={{ width: fieldWidth ? `${fieldWidth}px` : undefined }}
          className="z-[70] max-h-64 overflow-auto rounded-xl border border-zinc-200 bg-white p-1 shadow-lg ring-1 ring-black/5 [--anchor-gap:6px] focus:outline-none origin-top transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
        >
          {loading && results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-zinc-400">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-zinc-400">{emptyText}</div>
          ) : (
            results.map((option) => (
              <ComboboxOption
                key={getKey(option)}
                value={option}
                className="group flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 data-[focus]:bg-zinc-50"
              >
                <div className="flex min-w-0 flex-col items-start gap-0.5">
                  {renderOption(option)}
                </div>
                <CheckIcon className="size-4 shrink-0 text-primary-600 opacity-0 group-data-[selected]:opacity-100" />
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
}
